/**
 * @description
 * Settings service for managing platform settings.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date January 8, 2025
 */

import { BaseAPIClient } from "../core/base-client";
import type { Setting } from "./types";

/**
 * Settings service for managing platform settings
 */
export class SettingsService extends BaseAPIClient {
  /**
   * Get all settings
   */
  async getSettings(): Promise<Setting[]> {
    const data = await this.request<{ settings: Setting[] }>("/settings");
    return data.settings;
  }

  /**
   * Update a setting
   */
  async updateSetting(
    key: string,
    value: string,
    updatedBy?: string,
  ): Promise<Setting> {
    const response = await this.request<{ setting: Setting }>(
      `/settings/${key}`,
      {
        method: "PUT",
        body: JSON.stringify({ value, updatedBy }),
      },
    );
    return response.setting;
  }

  /**
   * Update multiple settings at once
   */
  async updateSettings(settings: Array<{
    key: string;
    value: string;
    category?: string;
    isSecret?: boolean;
    description?: string;
  }>): Promise<void> {
    await this.request('/settings/bulk', {
      method: 'PUT',
      body: JSON.stringify({ settings }),
    });
  }
}

// Export singleton instance
export const settingsService = new SettingsService();
